import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import {
  flex,
  height,
  maxWidth,
  space,
  width,
  type HeightProps
} from 'styled-system';

import NonStretchedImage from './non-stretched-image';

interface GatsbyFluidImage {
  aspectRatio: number;
  src: string;
  srcSet: string;
  sizes: string;
  base64?: string;
  tracedSVG?: string;
  srcWebp?: string;
  srcSetWebp?: string;
  media?: string;
}

interface ChildImageSharp {
  fluid?: GatsbyFluidImage;
}

interface LazyImageProps extends HeightProps {
  url?: string;
  fluid?: GatsbyFluidImage;
  childImageSharp?: ChildImageSharp;
  asBackground?: boolean;
  // Styled-system props
  width?: string | number;
  space?: string | number;
  maxWidth?: string | number;
  flex?: string | number;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
}

const imageStyles = css`
  ${height};
  ${width};
  ${space};
  ${maxWidth};
  ${flex};

  ${(props: LazyImageProps & { asBackground?: boolean }) =>
    props?.asBackground &&
    css`
      position: absolute !important;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    `};
`;

const Img = styled.img<LazyImageProps & { lazy?: boolean }>`
  ${imageStyles};
  display: block;
  width: 100%;
  object-fit: cover;
  object-position: 50% 50%;
  ${width};
`;

const StyledNonStretchedImage = styled(NonStretchedImage)<
  LazyImageProps & {
    placeholderStyle?: React.CSSProperties;
    imgStyle?: React.CSSProperties;
  }
>`
  ${imageStyles};
`;

const LazyImage = React.forwardRef<HTMLImageElement, LazyImageProps>(
  function LazyImage(props, ref) {
    const { url, fluid, childImageSharp, ...rest } = props;

    if (url) {
      return <Img src={url} {...rest} ref={ref} lazy />;
    }

    const fluidProps = useMemo(() => {
      return fluid || (childImageSharp && childImageSharp.fluid);
    }, [fluid, childImageSharp]);

    if (!fluidProps || typeof fluidProps == 'string') {
      return null;
    }

    return (
      <StyledNonStretchedImage
        placeholderStyle={{
          transition: 'opacity 0.65s ease-in 0.15s',
          filter: 'blur(10px)',
          left: '-2.5%',
          top: '-2.5%',
          width: '105%',
          height: '105%'
        }}
        imgStyle={{
          transition: 'opacity 0.55s ease-out'
        }}
        {...rest}
        fluid={fluidProps}
        ref={ref}
      />
    );
  }
);

export default LazyImage;
